<template>
  <v-row>
    <v-col cols="12">
      <v-card elevation="2" class="mb-4">
        <v-card-title>Meu Dashboard de Notas</v-card-title>
        <v-card-text>
          <!-- Evolução + Distribuição — visível assim que há dados do aluno -->
          <v-row v-if="studentRecords.length" class="mb-4">
            <v-col cols="12" md="8">
              <v-card elevation="2" height="100%">
                <v-card-title class="text-subtitle-1 font-weight-bold pb-0">
                  Evolução da Média por Período
                </v-card-title>
                <v-card-text>
                  <div style="height: 220px; position: relative;">
                    <Line
                      :data="periodEvolutionChartData"
                      :options="periodEvolutionChartOptions"
                    />
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card elevation="2" height="100%">
                <v-card-title class="text-subtitle-1 font-weight-bold pb-0">
                  Distribuição das Notas
                </v-card-title>
                <v-card-text>
                  <div style="height: 220px; position: relative;">
                    <Doughnut
                      :data="gradeDistributionData"
                      :options="gradeDistributionOptions"
                    />
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row v-if="studentTpRecords.length" class="mb-4">
            <v-col cols="12" md="8">
              <v-card elevation="2" height="100%">
                <v-card-title class="text-subtitle-1 font-weight-bold pb-0">
                  Evolução do Teste de Progresso por Período
                </v-card-title>
                <v-card-text>
                  <div style="height: 220px; position: relative;">
                    <Line
                      :data="tpEvolutionChartData"
                      :options="tpEvolutionChartOptions"
                    />
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
            <v-col cols="12" md="4">
              <v-card elevation="2" height="100%">
                <v-card-title class="text-subtitle-1 font-weight-bold pb-0">
                  Distribuição das Notas de TP
                </v-card-title>
                <v-card-text>
                  <div style="height: 220px; position: relative;">
                    <Doughnut
                      :data="tpGradeDistributionData"
                      :options="gradeDistributionOptions"
                    />
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mb-4" align="center">
            <v-col cols="12" md="4">
              <v-select
                v-model="selectedPeriod"
                :items="availablePeriods"
                label="Período"
                dense
                hide-details
              />
            </v-col>
          </v-row>

          <v-alert
            v-if="!this.effectiveDisciplineRecords.length"
            type="info"
            variant="tonal"
          >
            Nenhuma nota foi encontrada no banco de dados.
          </v-alert>

          <v-alert
            v-else-if="!studentRecords.length"
            type="info"
            variant="tonal"
          >
            Nenhuma nota encontrada para <strong>{{ studentName }}</strong>.
            Verifique se a planilha contém exatamente o mesmo nome ou
            username do aluno.
          </v-alert>

          <div v-else-if="!selectedPeriod">
            <v-alert type="info" variant="tonal">
              Selecione um período para ver suas médias, disciplinas e
              composição de notas.
            </v-alert>
          </div>

          <div v-else>
            <!-- KPI Cards + Relógios -->
            <v-row class="mb-4" align="stretch">
              <!-- KPI 2×2 -->
              <v-col cols="12" md="7">
                <v-row dense>
                  <v-col cols="6">
                    <v-card elevation="0" class="kpi-card kpi-card--blue pa-4">
                      <div class="kpi-label">Média Geral</div>
                      <div class="kpi-value">{{ formatNumber(overallAverage) }}</div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card elevation="0" class="kpi-card kpi-card--green pa-4">
                      <div class="kpi-label">Média no Período</div>
                      <div class="kpi-value">{{ formatNumber(studentSemesterAverage) }}</div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card elevation="0" class="kpi-card kpi-card--yellow pa-4">
                      <div class="kpi-label">Maior Nota</div>
                      <div class="kpi-value">{{ formatNumber(periodMaxGrade) }}</div>
                    </v-card>
                  </v-col>
                  <v-col cols="6">
                    <v-card elevation="0" class="kpi-card kpi-card--red pa-4">
                      <div class="kpi-label">Menor Nota</div>
                      <div class="kpi-value">{{ formatNumber(periodMinGrade) }}</div>
                    </v-card>
                  </v-col>
                </v-row>
              </v-col>

              <!-- Relógios -->
              <v-col cols="12" md="5">
                <v-card elevation="1" class="pa-4 fill-height d-flex flex-column justify-center">
                  <div class="text-caption text-center mb-2">Visão geral das médias</div>
                  <v-row justify="center" no-gutters>
                    <v-col
                      v-for="gauge in gaugeCharts"
                      :key="gauge.label"
                      cols="6"
                      class="text-center px-1"
                    >
                      <div style="height: 150px; position: relative;">
                        <Doughnut
                          :data="gauge.data"
                          :options="gauge.options"
                          :plugins="[centerTextPlugin]"
                        />
                      </div>
                      <div class="text-caption mt-1 gauge-label">{{ gauge.label }}</div>
                    </v-col>
                  </v-row>
                </v-card>
              </v-col>
            </v-row>

            <v-card elevation="2" class="mb-4" v-if="availableDisciplines.length">
              <v-card-title>Médias por disciplina</v-card-title>
              <v-card-text>
                <div class="chart-container">
                  <Bar
                    :data="averageByDisciplineChartData"
                    :options="averageByDisciplineChartOptions"
                  />
                </div>
              </v-card-text>
            </v-card>

            <v-row class="mb-4" align="center" v-if="availableDisciplines.length">
              <v-col cols="12" md="5">
                <v-select
                  v-model="selectedDiscipline"
                  :items="availableDisciplines"
                  :disabled="!availableDisciplines.length"
                  label="Disciplina"
                  dense
                  hide-details
                />
              </v-col>
            </v-row>

            <v-card elevation="2" class="mb-4">
              <v-card-title>Disciplinas do período</v-card-title>
              <v-card-text>
                <v-data-table
                  :headers="disciplineHeaders"
                  :items="studentDisciplineRows"
                  item-key="discipline"
                  dense
                  class="elevation-1"
                  @click:row="selectDiscipline"
                >
                  <template #item.average="{ item }">
                    {{ formatNumber(item.average) }}
                  </template>
                  <template #item.classAverage="{ item }">
                    {{ formatNumber(classAverageByDiscipline(item.discipline)) }}
                  </template>
                  <template #item.a1Absences="{ item }">
                    {{ valueOrDash(item.a1Absences) }}
                  </template>
                  <template #item.a2Absences="{ item }">
                    {{ valueOrDash(item.a2Absences) }}
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>

            <v-card elevation="2" class="mb-4" v-if="selectedDisciplineRecord">
              <v-card-title>
                Composição de notas - {{ selectedDisciplineRecord.discipline }}
              </v-card-title>
              <v-card-text>
                <v-row align="center">
                  <v-col cols="12" md="7">
                    <div class="chart-container">
                      <Bar
                        :data="breakdownChartData"
                        :options="breakdownChartOptions"
                      />
                    </div>
                    <v-row class="mt-3" dense>
                      <v-col cols="6">
                        <v-chip color="warning" variant="tonal" size="small">
                          Faltas A1: {{ valueOrDash(selectedDisciplineRecord.a1Absences) }}
                        </v-chip>
                      </v-col>
                      <v-col cols="6">
                        <v-chip color="warning" variant="tonal" size="small">
                          Faltas A2: {{ valueOrDash(selectedDisciplineRecord.a2Absences) }}
                        </v-chip>
                      </v-col>
                    </v-row>
                  </v-col>
                  <v-col cols="12" md="5">
                    <v-row no-gutters>
                      <v-col
                        v-for="gauge in disciplineGaugeCharts"
                        :key="gauge.label"
                        cols="6"
                        class="text-center px-1 mb-2"
                      >
                        <div style="height: 130px; position: relative;">
                          <Doughnut
                            :data="gauge.data"
                            :options="gauge.options"
                            :plugins="[centerTextPlugin]"
                          />
                        </div>
                        <div class="text-caption mt-1 gauge-label">{{ gauge.label }}</div>
                      </v-col>
                    </v-row>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>

            <v-alert v-if="selectedPeriod && !studentDisciplineRows.length" type="info" variant="tonal">
              Não há disciplinas registradas para o aluno neste período.
            </v-alert>
          </div>
        </v-card-text>
      </v-card>
    </v-col>
  </v-row>
</template>

<script>
import { Bar, Doughnut, Line } from "vue-chartjs";
import * as XLSX from "xlsx";
import DesempenhoDataService from "@/assets/js/services/DesempenhoDataService";
import {
  Chart as ChartJS,
  Title,
  Tooltip,
  Legend,
  ArcElement,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  CategoryScale,
  LinearScale,
} from "chart.js";

ChartJS.register(
  ArcElement,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Filler,
  Title,
  Tooltip,
  Legend
);

const centerTextPlugin = {
  id: "centerText",
  afterDraw(chart) {
    const opts = chart.options.centerText;
    if (!opts) return;
    const { ctx, chartArea } = chart;
    const cx = (chartArea.left + chartArea.right) / 2;
    const cy = (chartArea.top + chartArea.bottom) / 2;
    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (opts.dual) {
      ctx.font = "bold 9px sans-serif";
      ctx.fillStyle = "#888888";
      ctx.fillText(opts.period, cx, cy - 17);
      ctx.font = "bold 11px sans-serif";
      ctx.fillStyle = opts.medColor || "#1565c0";
      ctx.fillText(opts.medValue, cx, cy - 4);
      ctx.font = "bold 11px sans-serif";
      ctx.fillStyle = opts.tpColor || "#6E3C8F";
      ctx.fillText(opts.tpValue, cx, cy + 10);
    } else {
      ctx.font = "bold 16px sans-serif";
      ctx.fillStyle = "#333333";
      ctx.fillText(opts.value, cx, cy - 9);
      ctx.font = "9px sans-serif";
      ctx.fillStyle = "#777777";
      ctx.fillText(opts.label, cx, cy + 9);
    }
    ctx.restore();
  },
};

const academicPeriodSequence = ["1P", "2P", "3P", "4P", "5P", "6P", "7P", "8P", "9P", "10P", "11P", "12P"];

export default {
  name: "AlunoDashboard",
  components: { Bar, Doughnut, Line },
  props: {
    studentName: {
      type: String,
      default: "",
    },
    studentUsername: {
      type: String,
      default: "",
    },
    disciplineRecords: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    selectedPeriod: "",
    selectedDiscipline: "",
    centerTextPlugin,
    disciplineFile: null,
    disciplineName: "",
    disciplineError: "",
    storedDisciplineRecords: [],
    storedSummaryRecords: [],
    disciplineHeaders: [
      { title: "Disciplina", key: "discipline" },
      { title: "A1 final", key: "a1" },
      { title: "A2 final", key: "a2" },
      { title: "Média", key: "average" },
      { title: "Faltas A1", key: "a1Absences" },
      { title: "Faltas A2", key: "a2Absences" },
      { title: "Média turma", key: "classAverage" },
    ],
  }),
  computed: {
    normalizedStudentName() {
      return String(this.effectiveStudentName || "").trim().toLowerCase();
    },
    effectiveStudentName() {
      return String(
        this.studentName ||
          (this.$store && this.$store.state ? this.$store.state.userName : "") ||
          ""
      ).trim();
    },
    effectiveStudentUsername() {
      return String(
        this.studentUsername ||
          (this.$store && this.$store.state ? this.$store.state.userUsername : "") ||
          ""
      ).trim();
    },
    normalizedStudentUsername() {
      return String(this.effectiveStudentUsername || "").trim().toLowerCase();
    },
    effectiveDisciplineRecords() {
      return Array.isArray(this.disciplineRecords) && this.disciplineRecords.length
        ? this.disciplineRecords
        : this.storedDisciplineRecords;
    },
    studentTpRecords() {
      return this.storedSummaryRecords.filter((record) => {
        const recordName = String(record.studentName || "").trim().toLowerCase();
        return (
          record.series === "TP" &&
          (recordName === this.normalizedStudentName ||
            (this.normalizedStudentUsername && recordName === this.normalizedStudentUsername))
        );
      });
    },
    studentTpByPeriod() {
      return this.studentTpRecords.reduce((acc, record) => {
        const period = this.normalizeAcademicPeriod(record.period);
        if (!period) return acc;
        const value = Number(record.value);
        acc[period] = Number.isFinite(value) ? value : 0;
        return acc;
      }, {});
    },
    studentRecords() {
      return this.effectiveDisciplineRecords.filter((record) => {
        const recordName = String(record.studentName || "").trim().toLowerCase();
        return (
          recordName === this.normalizedStudentName ||
          (this.normalizedStudentUsername && recordName === this.normalizedStudentUsername)
        );
      });
    },
    availablePeriods() {
      return [...new Set(this.studentRecords.map((record) => record.period))].sort(
        (a, b) => this.periodSortValue(a) - this.periodSortValue(b)
      );
    },
    selectedProgressPeriod() {
      return this.nextProgressPeriod(this.selectedPeriod);
    },
    studentDisciplineRows() {
      if (!this.selectedPeriod) return [];
      return this.studentRecords.filter(
        (record) => record.period === this.selectedPeriod
      );
    },
    availableDisciplines() {
      return [...new Set(this.studentDisciplineRows.map((record) => record.discipline))];
    },
    selectedDisciplineRecord() {
      return (
        this.studentDisciplineRows.find(
          (record) => record.discipline === this.selectedDiscipline
        ) || this.studentDisciplineRows[0] || null
      );
    },
    averageByDisciplineChartData() {
      const labels = this.availableDisciplines;
      const data = labels.map((discipline) => {
        const record = this.studentDisciplineRows.find(
          (item) => item.discipline === discipline
        );
        return record?.average ?? 0;
      });
      return {
        labels,
        datasets: [
          {
            label: "Média por disciplina",
            data,
            backgroundColor: labels.map(() => "#3C6E8F"),
            borderRadius: 10,
          },
        ],
      };
    },
    averageByDisciplineChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          title: { display: true, text: "Média por disciplina" },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 10,
            ticks: { stepSize: 1 },
          },
          x: {
            grid: { display: false },
          },
        },
      };
    },
    studentSemesterAverage() {
      const values = this.studentDisciplineRows
        .map((record) => record.average)
        .filter((value) => value !== null && value !== undefined);
      return values.length
        ? values.reduce((sum, value) => sum + value, 0) / values.length
        : null;
    },
    studentTpAverage() {
      if (!this.selectedProgressPeriod) return null;
      return this.studentTpByPeriod[this.selectedProgressPeriod] ?? 0;
    },
    classPeriodAverage() {
      if (!this.selectedPeriod) return null;
      const classAverages = this.effectiveDisciplineRecords
        .filter((record) => record.period === this.selectedPeriod)
        .map((record) => record.average)
        .filter((value) => value !== null && value !== undefined);
      return classAverages.length
        ? classAverages.reduce((sum, value) => sum + value, 0) /
            classAverages.length
        : null;
    },
    overallAverage() {
      const values = this.studentRecords
        .map((r) => r.average)
        .filter((v) => v !== null && v !== undefined);
      return values.length
        ? values.reduce((s, v) => s + v, 0) / values.length
        : null;
    },
    periodMaxGrade() {
      const values = this.studentDisciplineRows
        .map((r) => r.average)
        .filter((v) => v !== null && v !== undefined);
      return values.length ? Math.max(...values) : null;
    },
    periodMinGrade() {
      const values = this.studentDisciplineRows
        .map((r) => r.average)
        .filter((v) => v !== null && v !== undefined);
      return values.length ? Math.min(...values) : null;
    },
    periodEvolutionChartData() {
      const sortedPeriods = [...new Set(this.studentRecords.map((r) => r.period))].sort(
        (a, b) => a.localeCompare(b)
      );
      const averages = sortedPeriods.map((period) => {
        const records = this.studentRecords.filter((r) => r.period === period);
        const values = records.map((r) => r.average).filter((v) => v !== null && v !== undefined);
        return values.length ? values.reduce((s, v) => s + v, 0) / values.length : null;
      });
      return {
        labels: sortedPeriods,
        datasets: [
          {
            label: "Média por Período",
            data: averages,
            borderColor: "#1565c0",
            backgroundColor: "rgba(21, 101, 192, 0.08)",
            fill: true,
            tension: 0.35,
            pointBackgroundColor: "#1565c0",
            pointBorderColor: "#ffffff",
            pointBorderWidth: 2,
            pointRadius: 6,
            pointHoverRadius: 8,
          },
        ],
      };
    },
    periodEvolutionChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => `Média: ${this.formatNumber(ctx.raw)}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 10,
            ticks: { stepSize: 2 },
            grid: { color: "rgba(0,0,0,0.06)" },
          },
          x: { grid: { display: false } },
        },
      };
    },
    tpEvolutionChartData() {
      const expectedRecords = this.availablePeriods
        .map((semesterPeriod) => {
          const tpPeriod = this.nextProgressPeriod(semesterPeriod);
          if (!tpPeriod) return null;
          return {
            period: tpPeriod,
            value: this.studentTpByPeriod[tpPeriod] ?? 0,
          };
        })
        .filter(Boolean);
      return {
        labels: expectedRecords.map((record) => record.period),
        datasets: [
          {
            label: "Teste de Progresso",
            data: expectedRecords.map((record) => record.value),
            borderColor: "#2e7d32",
            backgroundColor: "rgba(46, 125, 50, 0.08)",
            fill: true,
            tension: 0.35,
            pointBackgroundColor: "#2e7d32",
            pointBorderColor: "#ffffff",
            pointBorderWidth: 2,
            pointRadius: 6,
            pointHoverRadius: 8,
          },
        ],
      };
    },
    tpEvolutionChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => `TP: ${this.formatNumber(ctx.raw)}`,
            },
          },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 10,
            ticks: { stepSize: 2 },
            grid: { color: "rgba(0,0,0,0.06)" },
          },
          x: { grid: { display: false } },
        },
      };
    },
    gradeDistributionData() {
      const records = this.selectedPeriod ? this.studentDisciplineRows : this.studentRecords;
      const values = records.map((r) => r.average).filter((v) => v !== null && v !== undefined);
      const ranges = [
        { label: "0,0 – 2,9", min: 0,  max: 3,     color: "#d32f2f" },
        { label: "3,0 – 5,9", min: 3,  max: 6,     color: "#fb8c00" },
        { label: "6,0 – 7,9", min: 6,  max: 8,     color: "#1565c0" },
        { label: "8,0 – 10",  min: 8,  max: 10.01, color: "#388e3c" },
      ];
      return {
        labels: ranges.map((r) => r.label),
        datasets: [
          {
            data: ranges.map((r) => values.filter((v) => v >= r.min && v < r.max).length),
            backgroundColor: ranges.map((r) => r.color),
            borderWidth: 0,
            hoverOffset: 4,
          },
        ],
      };
    },
    tpGradeDistributionData() {
      return this.buildDistributionData(this.tpEvolutionChartData.datasets[0].data);
    },
    gradeDistributionOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "65%",
        plugins: {
          legend: {
            position: "bottom",
            labels: { boxWidth: 10, font: { size: 11 }, padding: 8 },
          },
          tooltip: {
            callbacks: {
              label: (ctx) => {
                const total = ctx.dataset.data.reduce((s, v) => s + v, 0);
                const pct = total ? Math.round((ctx.raw / total) * 100) : 0;
                return `${ctx.label}: ${ctx.raw} (${pct}%)`;
              },
            },
          },
        },
      };
    },
    gaugeCharts() {
      return [
        this.buildGaugeConfig(this.studentSemesterAverage, "MED. SEM.", "Media semestral"),
        this.buildGaugeConfig(
          this.studentTpAverage,
          "MED. TP",
          this.selectedProgressPeriod ? `Media TP ${this.selectedProgressPeriod}` : "Media TP"
        ),
      ];
    },
    disciplineGaugeCharts() {
      const rec = this.selectedDisciplineRecord;
      if (!rec) return [];
      const totalFaltas = (Number(rec.a1Absences) || 0) + (Number(rec.a2Absences) || 0);
      const a1Gauge =
        rec.a3a1 !== null && rec.a3a1 !== undefined
          ? this.buildDualGaugeConfig(rec.a1, rec.a3a1, "A1", "A1", "A3 de A1")
          : this.buildGaugeConfig(rec.a1, "A1", "Nota A1");
      const a2Gauge =
        rec.a3a2 !== null && rec.a3a2 !== undefined
          ? this.buildDualGaugeConfig(rec.a2, rec.a3a2, "A2", "A2", "A3 de A2")
          : this.buildGaugeConfig(rec.a2, "A2", "Nota A2");
      return [
        { ...a1Gauge, label: rec.a3a1 !== null && rec.a3a1 !== undefined ? "A1 / A3 de A1" : "Nota A1" },
        { ...a2Gauge, label: rec.a3a2 !== null && rec.a3a2 !== undefined ? "A2 / A3 de A2" : "Nota A2" },
        this.buildGaugeConfig(rec.average, "MÉDIA", "Média Final"),
        this.buildAbsenceGaugeConfig(totalFaltas, "FALTAS", "Total Faltas"),
      ];
    },
    totalAbsencesA1() {
      return this.studentDisciplineRows.reduce(
        (sum, record) => sum + (record.a1Absences || 0),
        0
      );
    },
    totalAbsencesA2() {
      return this.studentDisciplineRows.reduce(
        (sum, record) => sum + (record.a2Absences || 0),
        0
      );
    },
    breakdownChartData() {
      const record = this.selectedDisciplineRecord;
      if (!record) return { labels: [], datasets: [] };

      const components = [];
      if (record.componentsA1 && record.componentsA1.length > 0) {
        components.push(...record.componentsA1.map((item) => ({ ...item, side: "a1" })));
      } else if (record.a1 !== null && record.a1 !== undefined) {
        components.push({ label: "A1", value: record.a1, side: "a1" });
      }
      if (record.a3a1 !== null && record.a3a1 !== undefined) {
        components.push({ label: "A3 de A1", value: record.a3a1, isRecovery: true, side: "a1" });
      }
      if (record.componentsA2 && record.componentsA2.length > 0) {
        components.push(...record.componentsA2.map((item) => ({ ...item, side: "a2" })));
      } else if (record.a2 !== null && record.a2 !== undefined) {
        components.push({ label: "A2", value: record.a2, side: "a2" });
      }
      if (record.a3a2 !== null && record.a3a2 !== undefined) {
        components.push({ label: "A3 de A2", value: record.a3a2, isRecovery: true, side: "a2" });
      }

      return {
        labels: components.map((item) => item.label),
        datasets: [
          {
            label: "Composição de notas",
            data: components.map((item) => item.value),
            backgroundColor: components.map((item) => {
              if (item.isRecovery && item.side === "a2") return "#C2185B";
              if (item.isRecovery) return "#E65100";
              if (item.side === "a2") return "#6E3C8F";
              return "#3C6E8F";
            }),
            borderRadius: 10,
          },
        ],
      };
    },
    breakdownChartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: "top" },
          title: { display: false },
        },
        scales: {
          y: {
            beginAtZero: true,
            max: 10,
            ticks: { stepSize: 1 },
          },
          x: {
            grid: { display: false },
          },
        },
      };
    },
  },
  watch: {
    availablePeriods(newPeriods) {
      if (newPeriods.length && !newPeriods.includes(this.selectedPeriod)) {
        this.selectedPeriod = newPeriods[0];
      }
    },
    studentDisciplineRows(newRows) {
      if (newRows.length && !newRows.some((row) => row.discipline === this.selectedDiscipline)) {
        this.selectedDiscipline = newRows[0].discipline;
      }
    },
  },
  mounted() {
    this.loadDatabaseDisciplineRecords();
    this.loadStoredDisciplineRecords();
  },
  methods: {
    async loadDatabaseDisciplineRecords() {
      if (this.disciplineRecords && this.disciplineRecords.length) return;
      try {
        const params = {};
        const userId = this.$store?.state?.userId;
        if (userId) params.aluno_id = userId;
        const response = await DesempenhoDataService.dashboard(params);
        const records = response.data?.disciplineRecords || [];
        const summaryRecords = response.data?.summaryRecords || [];
        this.storedSummaryRecords = Array.isArray(summaryRecords) ? summaryRecords : [];
        if (Array.isArray(records) && records.length) {
          this.storedDisciplineRecords = records;
          this.disciplineName =
            response.data?.imports?.disciplinas?.arquivo || "Banco de dados";
          this.disciplineError = "";
          this.selectedPeriod = "";
          this.selectedDiscipline = "";
        }
      } catch (_error) {
        this.loadStoredDisciplineRecords();
      }
    },
    loadStoredDisciplineRecords() {
      if (this.disciplineRecords && this.disciplineRecords.length) return;
      try {
        const raw = localStorage.getItem("disciplineRecords");
        if (!raw) return;
        const stored = JSON.parse(raw);
        if (Array.isArray(stored) && stored.length) {
          this.storedDisciplineRecords = stored;
        }
      } catch (_error) {
        localStorage.removeItem("disciplineRecords");
      }
    },
    fileFromEvent(event) {
      if (Array.isArray(event)) return event[0];
      if (event?.target?.files?.length) return event.target.files[0];
      return event || null;
    },
    handleDisciplineSpreadsheetFile(event) {
      const file = this.fileFromEvent(event);
      if (!file) {
        this.disciplineFile = null;
        this.disciplineName = "";
        this.disciplineError = "";
        return;
      }
      const allowedTypes = [
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        "application/vnd.ms-excel",
      ];
      if (
        !allowedTypes.includes(file.type) &&
        !/\.(xls|xlsx)$/i.test(file.name)
      ) {
        this.disciplineError =
          "Formato inválido. Envie um arquivo .xls ou .xlsx.";
        this.disciplineFile = null;
        this.disciplineName = "";
        return;
      }
      this.disciplineError = "";
      this.disciplineFile = file;
      this.disciplineName = file.name;
      this.parseDisciplineSpreadsheetFile(file);
    },
    clearDisciplineSpreadsheet() {
      this.disciplineFile = null;
      this.disciplineName = "";
      this.disciplineError = "";
      this.storedDisciplineRecords = [];
      localStorage.removeItem("disciplineRecords");
      this.selectedPeriod = "";
      this.selectedDiscipline = "";
    },
    parseDisciplineSpreadsheetFile(file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const arrayBuffer = event.target.result;
          const workbook = XLSX.read(arrayBuffer, {
            type: "array",
            cellDates: true,
            raw: false,
          });

          const normalizeHeader = (value) =>
            String(value || "")
              .trim()
              .toLowerCase();

          const parseSheetRows = (sheet) =>
            XLSX.utils.sheet_to_json(sheet, {
              header: 1,
              raw: false,
              defval: "",
            });

          const findHeader = (rows) => {
            return rows.findIndex((row) => {
              const normalized = Array.isArray(row)
                ? row.map(normalizeHeader)
                : [];
              const hasPeriod = normalized.some((cell) =>
                ["periodo", "semestre", "period"].includes(cell)
              );
              const hasStudent = normalized.some((cell) =>
                ["estudante", "aluno", "nome", "alunos", "estudantes"].includes(
                  cell
                )
              );
              const hasDiscipline = normalized.some((cell) =>
                ["disciplina", "materia", "matéria", "subject"].includes(cell)
              );
              const hasA1 = normalized.some((cell) =>
                ["nota a1", "a1"].includes(cell)
              );
              const hasA2 = normalized.some((cell) =>
                ["nota a2", "a2"].includes(cell)
              );
              return (
                hasPeriod && hasStudent && hasDiscipline && (hasA1 || hasA2)
              );
            });
          };

          const normalizePeriod = (value) => {
            const raw = String(value || "")
              .trim()
              .toLowerCase();
            if (/1\s*º|1º|1\s*p|p1|primeiro|semestre 1|semestre01/.test(raw)) {
              return "1P";
            }
            if (/2\s*º|2º|2\s*p|p2|segundo|semestre 2|semestre02/.test(raw)) {
              return "2P";
            }
            if (/3\s*º|3º|3\s*p|p3|terceiro|semestre 3|semestre03/.test(raw)) {
              return "3P";
            }
            return raw.toUpperCase();
          };

          const parseGradeCell = (cell) => {
            const raw = String(cell || "").trim();
            if (raw === "") return null;
            const parsed = this.parseGradeValue(raw);
            return Number.isFinite(parsed) ? parsed : null;
          };

          const parseAbsenceCell = (cell, hasColumn) => {
            const raw = String(cell || "").trim();
            if (raw === "") return hasColumn ? 0 : null;
            const parsed = this.parseGradeValue(raw);
            return Number.isFinite(parsed) ? parsed : raw;
          };

          const findColumnIndex = (header, names) =>
            header.findIndex((cell) => names.includes(normalizeHeader(cell)));

          const findColumnIndexWithPatterns = (header, names, patterns) =>
            header.findIndex((cell) => {
              const normalized = normalizeHeader(cell);
              return (
                names.includes(normalized) ||
                patterns.some((pattern) => pattern.test(normalized))
              );
            });

          const buildDisciplineRecords = (rows, headerIndex) => {
            const header = Array.isArray(rows[headerIndex])
              ? rows[headerIndex]
              : [];
            const periodIndex = findColumnIndex(header, [
              "periodo",
              "semestre",
              "period",
            ]);
            const studentIndex = findColumnIndex(header, [
              "estudante",
              "aluno",
              "nome",
              "alunos",
              "estudantes",
            ]);
            const disciplineIndex = findColumnIndex(header, [
              "disciplina",
              "materia",
              "matéria",
              "subject",
            ]);
            const a1Index = findColumnIndex(header, ["nota a1", "a1"]);
            const a2Index = findColumnIndex(header, ["nota a2", "a2"]);
            const a3a1Index = findColumnIndex(header, [
              "a3 de a1",
              "a3a1",
              "a3 a1",
              "a3_de_a1",
            ]);
            const a3a2Index = findColumnIndex(header, [
              "a3 de a2",
              "a3a2",
              "a3 a2",
              "a3_de_a2",
            ]);
            const a1AbsenceIndex = findColumnIndexWithPatterns(
              header,
              [
                "faltas a1",
                "falta a1",
                "faltas a1",
                "a1 faltas",
                "a1 falta",
              ],
              [/(faltas|falta|ausencia|ausências|ausencias).*a1|a1.*(faltas|falta|ausencia|ausências|ausencias)/i]
            );
            const a2AbsenceIndex = findColumnIndexWithPatterns(
              header,
              [
                "faltas a2",
                "falta a2",
                "faltas a 2",
                "a2 faltas",
                "a2 falta",
                "a2 faltas",
              ],
              [
                /(faltas|falta|ausencia|ausências|ausencias).*a2|a2.*(faltas|falta|ausencia|ausências|ausencias)/i,
                /faltas.*2|2.*faltas/i,
              ]
            );
            const a1ComponentIndexes = header
              .map((cell, index) => ({ cell: normalizeHeader(cell), index }))
              .filter(
                ({ cell }) =>
                  /(?:teo|teoria|prat|prática|pbl|proj|trabalho).*a1|a1.*(?:teo|teoria|prat|prática|pbl|proj|trabalho)/i.test(
                    cell
                  ) &&
                  !/a3/.test(cell)
              )
              .map(({ index }) => index);
            const a2ComponentIndexes = header
              .map((cell, index) => ({ cell: normalizeHeader(cell), index }))
              .filter(
                ({ cell }) =>
                  /(?:teo|teoria|prat|prática|pbl|proj|trabalho).*a2|a2.*(?:teo|teoria|prat|prática|pbl|proj|trabalho)/i.test(
                    cell
                  ) &&
                  !/a3/.test(cell)
              )
              .map(({ index }) => index);

            return rows
              .slice(headerIndex + 1)
              .filter((row) =>
                Array.isArray(row)
                  ? row.some((cell) => String(cell || "").trim())
                  : false
              )
              .map((row, rowIndex) => {
                const values = Array.isArray(row) ? row : [];
                const period = normalizePeriod(values[periodIndex]);
                const studentName = String(values[studentIndex] || "").trim();
                const discipline = String(values[disciplineIndex] || "").trim();
                const a1Components = a1ComponentIndexes
                  .map((index) => ({
                    label: header[index] || `A1 comp ${index}`,
                    value: parseGradeCell(values[index]),
                  }))
                  .filter((item) => item.value !== null);
                const a2Components = a2ComponentIndexes
                  .map((index) => ({
                    label: header[index] || `A2 comp ${index}`,
                    value: parseGradeCell(values[index]),
                  }))
                  .filter((item) => item.value !== null);
                const rawA1 = a1Components.length
                  ? a1Components.reduce((sum, item) => sum + item.value, 0)
                  : parseGradeCell(values[a1Index]);
                const rawA2 = a2Components.length
                  ? a2Components.reduce((sum, item) => sum + item.value, 0)
                  : parseGradeCell(values[a2Index]);
                const recoveryA1 = parseGradeCell(values[a3a1Index]);
                const recoveryA2 = parseGradeCell(values[a3a2Index]);
                const a1Values = [rawA1, recoveryA1].filter(
                  (value) => value !== null
                );
                const a2Values = [rawA2, recoveryA2].filter(
                  (value) => value !== null
                );
                const effectiveA1 = a1Values.length
                  ? Math.max(...a1Values)
                  : null;
                const effectiveA2 = a2Values.length
                  ? Math.max(...a2Values)
                  : null;
                const a1Absences = parseAbsenceCell(
                  values[a1AbsenceIndex],
                  a1AbsenceIndex >= 0
                );
                const a2Absences = parseAbsenceCell(
                  values[a2AbsenceIndex],
                  a2AbsenceIndex >= 0
                );
                const finalAverage =
                  effectiveA1 !== null && effectiveA2 !== null
                    ? (effectiveA1 + effectiveA2) / 2
                    : effectiveA1 !== null
                    ? effectiveA1
                    : effectiveA2;

                return {
                  id: rowIndex + 1,
                  studentName,
                  period,
                  discipline,
                  a1: effectiveA1,
                  a2: effectiveA2,
                  a3a1: recoveryA1,
                  a3a2: recoveryA2,
                  componentsA1: a1Components,
                  componentsA2: a2Components,
                  a1Absences,
                  a2Absences,
                  average: finalAverage,
                };
              })
              .filter(
                (record) =>
                  record.studentName && record.discipline && record.period
              );
          };

          const disciplineRecords = [];
          for (const sheetName of workbook.SheetNames) {
            const sheet = workbook.Sheets[sheetName];
            const rows = parseSheetRows(sheet);
            const headerIndex = findHeader(rows);
            if (headerIndex >= 0) {
              disciplineRecords.push(
                ...buildDisciplineRecords(rows, headerIndex)
              );
            }
          }

          if (!disciplineRecords.length) {
            throw new Error(
              "Não foi possível localizar registros de disciplina válidos na planilha. Verifique os cabeçalhos."
            );
          }

          this.storedDisciplineRecords = disciplineRecords;
          this.disciplineError = "";
          localStorage.setItem(
            "disciplineRecords",
            JSON.stringify(disciplineRecords)
          );
          this.selectedPeriod = "";
          this.selectedDiscipline = "";
        } catch (error) {
          this.storedDisciplineRecords = [];
          this.disciplineError =
            error?.message ||
            "Erro ao processar a planilha de disciplinas. Verifique o formato do arquivo.";
          localStorage.removeItem("disciplineRecords");
        }
      };
      reader.readAsArrayBuffer(file);
    },
    selectDiscipline(record) {
      this.selectedDiscipline = record.discipline;
    },
    buildAbsenceGaugeConfig(value, centerLabel, fullLabel) {
      const MAX = 20;
      const filled = Math.min(Math.max(value || 0, 0), MAX);
      const color = value <= 3 ? "#388e3c" : value <= 7 ? "#fb8c00" : "#d32f2f";
      return {
        label: fullLabel,
        data: {
          datasets: [
            {
              data: [filled, MAX - filled],
              backgroundColor: [color, "#e0e0e0"],
              borderWidth: 0,
              hoverOffset: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "72%",
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
          centerText: { value: String(value), label: centerLabel },
          animation: { duration: 400 },
        },
      };
    },
    performanceColor(value) {
      if (value === null || value === undefined) return "#e0e0e0";
      if (value < 3) return "#d32f2f";
      if (value < 6) return "#fb8c00";
      return "#1565c0";
    },
    buildDistributionData(values) {
      const numericValues = values.filter((v) => v !== null && v !== undefined);
      const ranges = [
        { label: "0,0 - 2,9", min: 0, max: 3, color: "#d32f2f" },
        { label: "3,0 - 5,9", min: 3, max: 6, color: "#fb8c00" },
        { label: "6,0 - 7,9", min: 6, max: 8, color: "#1565c0" },
        { label: "8,0 - 10", min: 8, max: 10.01, color: "#388e3c" },
      ];
      return {
        labels: ranges.map((r) => r.label),
        datasets: [
          {
            data: ranges.map((r) => numericValues.filter((v) => v >= r.min && v < r.max).length),
            backgroundColor: ranges.map((r) => r.color),
            borderWidth: 0,
            hoverOffset: 4,
          },
        ],
      };
    },
    periodSortValue(period) {
      const match = String(period || "").match(/\d+/);
      return match ? Number(match[0]) : 999;
    },
    nextProgressPeriod(period) {
      const index = academicPeriodSequence.indexOf(this.normalizeAcademicPeriod(period));
      return index >= 0 ? academicPeriodSequence[index + 1] || null : null;
    },
    normalizeAcademicPeriod(period) {
      const raw = String(period || "").trim().toUpperCase();
      if (!raw) return "";
      const match = raw.match(/\d+/);
      return match ? `${Number(match[0])}P` : raw;
    },
    buildGaugeConfig(value, centerLabel, fullLabel) {
      const hasValue = value !== null && value !== undefined;
      const color = this.performanceColor(value);
      const filled = hasValue ? Math.min(Math.max(value, 0), 10) : 0;
      const displayValue = hasValue
        ? value.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "-";
      return {
        label: fullLabel,
        data: {
          datasets: [
            {
              data: hasValue ? [filled, 10 - filled] : [10, 0],
              backgroundColor: hasValue ? [color, "#e0e0e0"] : ["#e0e0e0", "#e0e0e0"],
              borderWidth: 0,
              hoverOffset: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "72%",
          plugins: {
            legend: { display: false },
            tooltip: { enabled: false },
          },
          centerText: { value: displayValue, label: centerLabel },
          animation: { duration: 400 },
        },
      };
    },
    buildDualGaugeConfig(medValue, tpValue, period, outerLabel = "Média Sem.", innerLabel = "Média TP") {
      const fmt = (value) =>
        value !== null && value !== undefined
          ? value.toLocaleString("pt-BR", {
              minimumFractionDigits: 1,
              maximumFractionDigits: 2,
            })
          : "-";
      const fill = (value) =>
        value !== null && value !== undefined ? Math.min(Math.max(value, 0), 10) : 0;
      const medColor = this.performanceColor(medValue);
      const tpColor = this.performanceColor(tpValue);
      const medHas = medValue !== null && medValue !== undefined;
      const tpHas = tpValue !== null && tpValue !== undefined;
      return {
        label: period,
        data: {
          datasets: [
            {
              data: medHas ? [fill(medValue), 10 - fill(medValue)] : [10, 0],
              backgroundColor: medHas
                ? [medColor, "#e8e8e8"]
                : ["#e0e0e0", "#e0e0e0"],
              borderWidth: 2,
              borderColor: "#ffffff",
              hoverOffset: 0,
            },
            {
              data: tpHas ? [fill(tpValue), 10 - fill(tpValue)] : [10, 0],
              backgroundColor: tpHas
                ? [tpColor, "#e8e8e8"]
                : ["#e0e0e0", "#e0e0e0"],
              borderWidth: 2,
              borderColor: "#ffffff",
              hoverOffset: 0,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          cutout: "52%",
          plugins: {
            legend: { display: false },
            tooltip: {
              callbacks: {
                label: (ctx) => {
                  if (ctx.dataIndex === 1) return null;
                  const name = ctx.datasetIndex === 0 ? outerLabel : innerLabel;
                  const value = ctx.datasetIndex === 0 ? fmt(medValue) : fmt(tpValue);
                  return `${name}: ${value}`;
                },
              },
            },
          },
          centerText: {
            dual: true,
            period,
            medValue: fmt(medValue),
            tpValue: fmt(tpValue),
            medColor,
            tpColor,
          },
          animation: { duration: 400 },
        },
      };
    },
    formatNumber(value) {
      return value === null || value === undefined
        ? "-"
        : Number(value).toLocaleString("pt-BR", {
            minimumFractionDigits: 1,
            maximumFractionDigits: 2,
          });
    },
    parseGradeValue(value) {
      const raw = String(value || "").trim();
      if (raw === "") {
        return null;
      }

      const normalized = raw
        .replace(/,/g, ".")
        .replace(/\s+/g, "")
        .replace(/[,;]$/g, "")
        .replace(/–/g, "-")
        .replace(/[^0-9.\-]/g, "");

      const parsed = Number(normalized);
      return Number.isFinite(parsed) ? parsed : null;
    },
    valueOrDash(value) {
      return value === null || value === undefined ? "-" : value;
    },
    classAverageByDiscipline(discipline) {
      if (!this.selectedPeriod) return null;
      const values = this.effectiveDisciplineRecords
        .filter(
          (record) =>
            record.period === this.selectedPeriod &&
            record.discipline === discipline
        )
        .map((record) => record.average)
        .filter((value) => value !== null && value !== undefined);
      return values.length
        ? values.reduce((sum, value) => sum + value, 0) / values.length
        : null;
    },
  },
};
</script>

<style scoped>
.chart-container {
  min-height: 280px;
}
.gauge-label {
  font-size: 10px;
  color: #555;
  word-break: break-word;
}

/* KPI cards */
.kpi-card {
  border-radius: 12px;
}
.kpi-label {
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 6px;
}
.kpi-value {
  font-size: 1.7rem;
  font-weight: 700;
  line-height: 1;
}
.kpi-card--blue  { background: #e3f2fd; }
.kpi-card--blue  .kpi-label, .kpi-card--blue  .kpi-value { color: #1565c0; }
.kpi-card--green { background: #e8f5e9; }
.kpi-card--green .kpi-label, .kpi-card--green .kpi-value { color: #2e7d32; }
.kpi-card--yellow { background: #fff8e1; }
.kpi-card--yellow .kpi-label, .kpi-card--yellow .kpi-value { color: #e65100; }
.kpi-card--red   { background: #fce4ec; }
.kpi-card--red   .kpi-label, .kpi-card--red   .kpi-value { color: #c62828; }
</style>
